import { Box, LinearProgress, Stack } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { BodyText, CaptionText } from '../../shared/typography/PrimaryTypography';

/**
 * Share of a record set a human has approved, as a whole percent in `[0, 100]`.
 * An empty set is 0% (nothing to be done yet), never a divide-by-zero. 100 is
 * reserved for literally-every-record-approved: 999/1000 must read 99, not round
 * up to a "100% reviewed" that contradicts the outstanding count beside it.
 * @param {number} approved Records approved (numerator).
 * @param {number} total Records in the set (denominator).
 * @returns {number} The approved percentage.
 */
export function approvedPercent(approved: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  if (approved >= total) {
    return 100;
  }
  return Math.min(99, Math.floor((approved / total) * 100));
}

interface CompletenessMeterProps {
  /** Records a human has approved (the numerator). */
  approved: number;
  /** Records in the set (the denominator). */
  total: number;
  /**
   * `full` for the panel header (big % + counts); `compact` for a metric-group
   * header (a thin inline bar + fraction).
   */
  variant?: 'full' | 'compact';
}

/**
 * "How done is this set" — a determinate progress bar plus counts that turns
 * success-green the moment every record is approved. This is the completeness
 * signal Maxine asked for: approved records fill the bar toward 100%.
 * @param {CompletenessMeterProps} props Approved/total counts and the size variant.
 * @returns {JSX.Element}
 */
export default function CompletenessMeter({
  approved,
  total,
  variant = 'full'
}: CompletenessMeterProps) {
  const percent = approvedPercent(approved, total);
  const complete = total > 0 && approved >= total;
  const color = complete ? 'success' : 'primary';

  // The accessible name/value live on the LinearProgress: it renders
  // role="progressbar", so assistive tech actually exposes them — an aria-label
  // on a roleless Box/Stack is never announced.
  if (variant === 'compact') {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={color}
          aria-label="Review completeness"
          aria-valuetext={`${approved} of ${total} approved`}
          sx={{ flex: 1, height: 6, borderRadius: 3 }}
        />
        <CaptionText>
          {approved}/{total}
        </CaptionText>
      </Stack>
    );
  }

  return (
    <Box sx={{ minWidth: 200 }}>
      <Stack direction="row" spacing={0.75} alignItems="baseline" justifyContent="space-between">
        <Stack direction="row" spacing={0.5} alignItems="center">
          {complete && (
            <CheckCircleRoundedIcon color="success" sx={{ fontSize: 18 }} aria-hidden="true" />
          )}
          <BodyText weight="Bold">{percent}% reviewed</BodyText>
        </Stack>
        <CaptionText>
          {approved} of {total} approved
        </CaptionText>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={color}
        aria-label="Review completeness"
        aria-valuetext={`${approved} of ${total} approved`}
        sx={{ mt: 0.75, height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}
